import { Avatar } from "@/components/ui";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoChatParticipation } from "@/shared/model/edo-chat-participation.model";
import { EdoChatType } from "@/shared/model/enumerations/edo-chat-type.model";
import {
  ChatState,
  closeChatWindow,
  openChatWindow,
} from "@/shared/reducers/chat.reducer";
import { cn } from "@/shared/util/cn";
import { Users, X } from "lucide-react";
import { useMemo } from "react";
import FloatingChatWindow from "./FloatingChatWindow";

const WINDOW_WIDTH = 320;
const WINDOW_GAP = 16;
const RIGHT_MARGIN = 24;

const FloatingChatDock: React.FC = () => {
  const dispatch = useAppDispatch();
  const { openChatIds, minimizedChatIds, participations } = useAppSelector<ChatState>(
    (s) => s.chat
  );

  const minimizedParticipations = useMemo(() => {
    return minimizedChatIds
      .map<IEdoChatParticipation | undefined>((id) =>
        participations.find((p) => p.chat?.id === id)
      )
      .filter((x): x is IEdoChatParticipation => Boolean(x));
  }, [minimizedChatIds, participations]);

  if (openChatIds.length === 0 && minimizedChatIds.length === 0) {
    return null;
  }

  // Reserve a column on the right for the minimized bubbles when present
  const bubbleColumn = minimizedChatIds.length > 0 ? 56 + WINDOW_GAP : 0;

  return (
    <>
      {openChatIds.map((id, idx) => {
        const right = RIGHT_MARGIN + bubbleColumn + idx * (WINDOW_WIDTH + WINDOW_GAP);
        return <FloatingChatWindow key={id} chatId={id} rightOffset={right} />;
      })}

      {minimizedChatIds.length > 0 && (
        <div
          className="pointer-events-auto fixed bottom-4 z-40 flex flex-col gap-2"
          style={{ right: `${RIGHT_MARGIN}px` }}
        >
          {minimizedParticipations.map((p) => {
            const isGroup = p.chat?.type === EdoChatType.GROUP;
            const title = isGroup ? p.chat?.title : p.title;
            const initials =
              title
                ?.split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map((s) => s[0]?.toUpperCase())
                .join("") || "?";
            return (
              <div
                key={p.chat?.id}
                className="group relative"
                title={title}
              >
                <button
                  type="button"
                  onClick={() => p.chat?.id != null && dispatch(openChatWindow(p.chat.id))}
                  className={cn(
                    "flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-[var(--color-mockup-border)] bg-surface shadow-card transition-transform hover:scale-105 hover:shadow-card-hover"
                  )}
                  aria-label={`Apri chat ${title ?? ""}`}
                >
                  <Avatar className="h-10 w-10 text-xs">
                    {isGroup ? <Users className="h-4 w-4" /> : initials}
                  </Avatar>
                </button>
                <button
                  type="button"
                  onClick={() => p.chat?.id != null && dispatch(closeChatWindow(p.chat.id))}
                  aria-label="Rimuovi"
                  className="absolute -right-1 -top-1 hidden h-5 w-5 cursor-pointer items-center justify-center rounded-full border border-[var(--color-mockup-border)] bg-surface text-[var(--color-mockup-text-mid)] shadow-sm transition-colors hover:bg-[var(--color-mockup-coral)]/10 hover:text-[var(--color-mockup-coral)] group-hover:flex"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default FloatingChatDock;
